/** @jsxImportSource preact */
import { createContext, type ComponentChildren } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

import * as copy from "../../paraglide/messages.js";
import { FORM_STYLES } from "../form/form-styles";
import { CloseIcon } from "../icons";

const FOCUSABLE =
  'button:not(:disabled), a[href], input:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * 聊天窗里面板的外壳样式：遮罩盖住整个聊天窗，面板从底部升起。
 * 颜色和圆角读 formVars 给的 --gn-form-*，底色读 --gn-sheet-bg（写进 background，必须不透明：毛玻璃面板上不能透出下面的消息）。
 * 面板里的内容各自带样式：加购面板见 cart-styles，请求浮层见 contact/request-overlay。
 */
export const PANEL_SHEET_STYLES = `
.gn-sheet-layer { position:absolute; inset:0; z-index:20; display:flex; flex-direction:column; justify-content:flex-end; --gn-sheet-ctl:40px; }
.gn-sheet-layer[data-touch] { --gn-sheet-ctl:44px; }
.gn-sheet-scrim { position:absolute; inset:0; border:0; padding:0; background:rgba(0,0,0,.32); cursor:default; animation:gnSheetFade .18s ease-out both; }
.gn-sheet { position:relative; box-sizing:border-box; display:flex; flex-direction:column; gap:16px; max-height:88%; overflow-y:auto; overscroll-behavior:contain; scrollbar-width:thin; scrollbar-color:var(--gn-form-line) transparent; padding:16px 16px calc(16px + env(safe-area-inset-bottom, 0px)); border-radius:var(--gn-form-r-card) var(--gn-form-r-card) 0 0; background:var(--gn-sheet-bg); color:var(--gn-form-ink); font-size:14px; line-height:1.4; text-align:left; box-shadow:0 -8px 24px rgba(0,0,0,.14); animation:gnSheetUp .22s cubic-bezier(.2,.8,.2,1) both; }
.gn-sheet:focus { outline:none; }
.gn-sheet .gn-btn { min-height:48px; font-size:14px; }
.gn-sheet-layer[data-touch] .gn-sheet .gn-btn { min-height:52px; }
.gn-sheet-close { flex:none; display:flex; align-items:center; justify-content:center; width:var(--gn-sheet-ctl); height:var(--gn-sheet-ctl); padding:0; border:0; border-radius:var(--gn-form-r-pill); background:transparent; color:var(--gn-form-quiet); cursor:pointer; }
.gn-sheet-close:hover:not(:disabled) { background:var(--gn-form-chip); color:var(--gn-form-ink); }
.gn-sheet-close:disabled { opacity:.4; cursor:default; }
@keyframes gnSheetUp { from { transform:translateY(24px); opacity:.3; } to { transform:none; opacity:1; } }
@keyframes gnSheetFade { from { opacity:0; } to { opacity:1; } }
@media (prefers-reduced-motion: reduce) { .gn-sheet, .gn-sheet-scrim { animation:none; } }
`;

/**
 * 聊天窗给消息里的卡片开面板用：面板挂进聊天窗这一层，才能盖住整个聊天窗。
 * 没有聊天窗（设计系统画板、后台回放）时没有这个值，卡片就在原地展开。
 */
export interface PanelSheetHost {
  /** 面板挂载的位置：聊天窗本身。 */
  element: HTMLElement;
  /** 触屏：控件放大到 44px。 */
  touch: boolean;
  /** 面板的实底色。 */
  background: string;
}

export const PanelSheetHostContext = createContext<PanelSheetHost | undefined>(
  undefined,
);

export interface PanelSheetLayerProps {
  /** 面板标题的元素 id，读屏打开面板时读它。 */
  labelledBy: string;
  /** 触屏：控件放大到 44px。 */
  touch: boolean;
  /** 写入中：不能关，遮罩和 Esc 都不理。 */
  locked: boolean;
  onDismiss: () => void;
  style: Record<string, string | number>;
  children: ComponentChildren;
}

/**
 * 聊天窗里的面板：遮罩盖住整个聊天窗，面板从底部升起。打开期间聊天窗其余部分不可操作（inert），
 * 焦点留在面板里；Esc 只关面板，不往外冒——手机上聊天窗本身也在抽屉里，冒出去会连抽屉一起关。
 */
export function PanelSheetLayer({
  labelledBy,
  touch,
  locked,
  onDismiss,
  style,
  children,
}: PanelSheetLayerProps) {
  const layer = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const parent = layer.current?.parentElement;
    const siblings = Array.from(parent?.children ?? []).filter(
      (element) => element !== layer.current && !element.hasAttribute("inert"),
    );
    for (const element of siblings) element.setAttribute("inert", "");
    sheet.current?.focus({ preventScroll: true });
    return () => {
      for (const element of siblings) element.removeAttribute("inert");
    };
  }, []);
  return (
    <div
      class="gn-sheet-layer"
      data-base-ui-swipe-ignore=""
      data-touch={touch || undefined}
      ref={layer}
      style={style}
    >
      <style>{`${FORM_STYLES}${PANEL_SHEET_STYLES}`}</style>
      <button
        aria-hidden="true"
        class="gn-sheet-scrim"
        onClick={() => {
          if (!locked) onDismiss();
        }}
        tabIndex={-1}
        type="button"
      />
      <div
        aria-busy={locked || undefined}
        aria-labelledby={labelledBy}
        aria-modal="true"
        class="gn-sheet"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            if (!locked) onDismiss();
            return;
          }
          if (event.key !== "Tab" || !sheet.current) return;
          const focusable = Array.from(
            sheet.current.querySelectorAll<HTMLElement>(FOCUSABLE),
          );
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (!first || !last) return;
          const root = sheet.current.getRootNode();
          const active =
            root instanceof ShadowRoot || root instanceof Document
              ? root.activeElement
              : null;
          if (
            event.shiftKey &&
            (active === first || active === sheet.current)
          ) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        ref={sheet}
        role="dialog"
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}

/** 面板右上角的关闭键。 */
export function PanelSheetClose({
  disabled = false,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={copy.widget_sheet_close()}
      class="gn-sheet-close"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <CloseIcon size={18} strokeWidth={2} />
    </button>
  );
}
